import { useId, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FeedbackIcon from "@mui/icons-material/Feedback";
import SendIcon from "@mui/icons-material/Send";
import supportClient from "@/shared/api/clients/supportClient";
import { DialogShell } from "@/shared/ui/DialogShell";

const MAX_MESSAGE_LENGTH = 1000;
const categories = [
  {
    value: "request_help_technical_issue",
    label: "Request help / Technical issue",
    placeholder:
      "Describe the issue, what you were trying to do, and any error messages.",
  },
  {
    value: "feedback",
    label: "General feedback",
    placeholder: "What's working well? What could be improved?",
  },
  {
    value: "feature_request",
    label: "Feature request",
    placeholder:
      "Tell us about the feature and how it would help your workflow.",
  },
  {
    value: "other",
    label: "Other",
    placeholder: "Please provide as much detail as possible.",
  },
] as const;

type IssueType = (typeof categories)[number]["value"];

/** Available throughout the signed-in app, including denied-access screens. */
export function ReportIssueWidget() {
  const { user } = useAuth();
  const titleId = useId();
  const descriptionId = useId();
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState<IssueType>(categories[0].value);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  // A synchronous guard also covers a second submit before React rerenders.
  const submittingRef = useRef(false);

  function handleClose() {
    if (!submittingRef.current) setOpen(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (submittingRef.current || !trimmedMessage) return;

    submittingRef.current = true;
    setSubmitting(true);
    setError(false);
    setSuccess(false);

    try {
      await supportClient.post("/support", {
        type: issueType,
        message: trimmedMessage,
        url: window.location.href.slice(0, 2048),
        path: window.location.pathname.slice(0, 2048),
        userAgent: navigator.userAgent.slice(0, 1024),
        ...(user?.id ? { userId: user.id } : {}),
      });
      setMessage("");
      setSuccess(true);
    } catch {
      // Keep the category and draft intact so a failed request can be retried.
      setError(true);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <>
      <Tooltip title="Help & Feedback" placement="left" arrow>
        <Fab
          color="primary"
          aria-label="open feedback panel"
          aria-haspopup="dialog"
          onClick={() => {
            setError(false);
            setSuccess(false);
            setOpen(true);
          }}
          sx={{ position: "fixed", bottom: 20, right: 16, zIndex: 1001 }}
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>

      <DialogShell
        open={open}
        onClose={handleClose}
        closeDisabled={submitting}
        showCloseButton={false}
        titleId={titleId}
        descriptionId={descriptionId}
        size="xs"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-end",
            justifyContent: "flex-end",
          },
        }}
        paperSx={{
          m: 2,
          mb: "88px",
          width: "calc(100% - 32px)",
          maxHeight: "calc(100dvh - 104px)",
          borderRadius: 3,
        }}
      >
        <DialogTitle
          id={titleId}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            py: 1.25,
          }}
        >
          <FeedbackIcon />
          <Box component="span" sx={{ flexGrow: 1 }}>
            Help & Feedback
          </Box>
          <IconButton
            aria-label="close feedback panel"
            color="inherit"
            size="small"
            onClick={handleClose}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box
          component="form"
          onSubmit={handleSubmit}
          aria-busy={submitting}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <DialogContent sx={{ pt: 2.5 }}>
            <Typography
              id={descriptionId}
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2.5 }}
            >
              Get help with Investigator, report a problem, or share an idea.
            </Typography>
            <TextField
              select
              required
              fullWidth
              label="Category"
              size="small"
              value={issueType}
              disabled={submitting}
              onChange={(event) =>
                setIssueType(event.target.value as IssueType)
              }
              sx={{ mb: 2.5 }}
            >
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              minRows={4}
              maxRows={7}
              label="Message"
              value={message}
              disabled={submitting}
              onChange={(event) => {
                setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH));
                setSuccess(false);
              }}
              placeholder={
                categories.find((category) => category.value === issueType)
                  ?.placeholder
              }
              helperText={`${message.length}/${MAX_MESSAGE_LENGTH} characters`}
              slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_LENGTH } }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to submit. Your message has been kept. Please try again.
              </Alert>
            )}
            {success && (
              <Alert severity="success" role="status" sx={{ mt: 2 }}>
                Thanks! Your message was submitted successfully.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleClose} disabled={submitting}>
              {success ? "Close" : "Cancel"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !message.trim()}
              endIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {submitting ? "Sending..." : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </DialogShell>
    </>
  );
}
